import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ART, BRAND, CONTACT, EXPERIENCES, INTRO, OFFERINGS, PRINCIPLES, FOUNDER_ROLE, FOUNDER_STORY, RECOGNITION, RECOGNITION_LOGOS } from "../data/experiences";

const Hero = () => (
  <section className="home-hero" data-testid="hero-section">
    <div className="editorial-container hero-layout">
      <div className="hero-copy">
        <p className="editorial-label mb-7" data-testid="hero-supporting-line">Heritage walks, cultural experiences and stories from Delhi and beyond.</p>
        <h1 className="font-accent text-4xl sm:text-5xl lg:text-6xl text-maroon leading-[1.12]" data-testid="hero-headline">Discover the cities beneath the cities.</h1>
        <p className="editorial-body mt-7 max-w-lg" data-testid="hero-subheadline">Step into nine centuries of empire, faith, poetry, and everyday life, guided by someone who was raised inside its lanes, not trained to walk them.</p>
        <div className="mt-9 flex flex-wrap items-center gap-5">
          <Link to="/contact" className="editorial-button" data-testid="hero-book-walk-button">Book a Walk <ArrowRight size={16} /></Link>
          <Link to="/experiences" className="editorial-link" data-testid="hero-explore-experiences-button">Explore Experiences <ArrowRight size={15} /></Link>
        </div>
      </div>
      <figure className="hero-artwork">
        <img src={ART.courtyard} alt="A painting of a Delhi courtyard, framed by a scalloped arch" width="735" height="935" className="artwork-image" fetchPriority="high" data-testid="hero-cover-image" />
        <figcaption className="artwork-caption" data-testid="hero-image-caption">Delhi, through another lens.</figcaption>
      </figure>
    </div>
  </section>
);

const Introduction = () => (
  <section className="editorial-section bg-white" data-testid="about-intro-section">
    <div className="editorial-container narrative-layout">
      <div className="intro-identity" data-testid="intro-identity-block">
        <div className="intro-brand-lockup" data-testid="intro-brand-lockup">
          <img src={BRAND.star} alt="Complete eight-pointed Unzip Delhi brand motif" width="576" height="538" className="brand-star" loading="lazy" data-testid="complete-brand-star" />
          <p lang="ur" dir="rtl" className="intro-brand-urdu" data-testid="intro-urdu-calligraphy">{BRAND.urdu}</p>
        </div>
        <h2 className="editorial-heading" data-testid="about-intro-heading">Told From the Inside, Not the Outside</h2>
      </div>
      <div className="editorial-prose" data-testid="about-intro-copy">
        {INTRO.map((text, i) => <p key={i} className={i === 1 || i === 3 ? "text-maroon font-medium" : ""} data-testid={`intro-paragraph-${i}`}>{text}</p>)}
      </div>
    </div>
  </section>
);

const Offerings = () => (
  <section className="editorial-section" data-testid="ways-to-experience-section">
    <div className="editorial-container">
      <h2 className="editorial-heading mb-12" data-testid="ways-to-experience-heading">Ways to Experience the City</h2>
      <div className="offerings-layout">
        <figure className="offerings-artwork">
          <img src={ART.jeweller} alt="Painting of a Delhi jeweller at work beneath a striped awning" width="517" height="720" loading="lazy" className="artwork-image" data-testid="offerings-artwork" />
          <figcaption className="artwork-caption">Delhi jeweller, 1867. Supplied artwork.</figcaption>
        </figure>
        <div className="divide-y divide-maroon/20 border-y border-maroon/20">
          {OFFERINGS.map((item, i) => (
            <article className="py-8 first:pt-6" key={item.title} data-testid={`offering-${i}`}>
              <h3 className="font-serif text-2xl text-maroon mb-4">{item.title}</h3>
              <p className="editorial-body">{item.text}</p>
            </article>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Principles = () => (
  <section className="editorial-section bg-obsidian text-ivory" data-testid="principles-section">
    <div className="editorial-container">
      <div className="narrative-layout mb-12">
        <h2 className="editorial-heading text-gold" data-testid="principles-heading">What Makes Unzip Delhi Different?</h2>
        <div className="editorial-prose text-ivory/80">
          <p className="font-serif text-3xl text-ivory" data-testid="principles-lead">We don't do checklist tourism.</p>
          <p>We believe the best way to understand a place is through its stories, its people and the layers of history that continue to shape everyday life.</p>
          <p>Our experiences are:</p>
        </div>
      </div>
      <div className="divide-y divide-ivory/20 border-y border-ivory/20">
        {PRINCIPLES.map((item, i) => (
          <div className="principle-row" key={item.title} data-testid={`principle-${i}`}>
            <h3 className="editorial-label text-gold">{item.title}</h3>
            <p className="editorial-body text-ivory/80">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FounderIntroduction = () => (
  <section className="editorial-section" data-testid="founder-introduction-section">
    <div className="editorial-container">
      <p className="editorial-label mb-8" data-testid="founder-section-label">WALK WITH SOMEONE WHO KNOWS THE CITY</p>
      <div className="narrative-layout">
        <div>
          <h2 className="editorial-heading" data-testid="founder-introduction-heading">Meet Anas Khan<span lang="ur" dir="rtl" className="urdu-founder-name" data-testid="founder-name-urdu">انَس خان</span></h2>
          <p className="mt-5 text-sm leading-relaxed text-maroon max-w-xs" data-testid="founder-role">{FOUNDER_ROLE}</p>
          <figure className="founder-portrait-frame" data-testid="home-founder-portrait-figure">
            <img src={ART.founder} alt="Anas Khan, founder of Unzip Delhi, overlooking the city at night" width="1500" height="2000" loading="lazy" className="artwork-image" data-testid="home-founder-portrait" />
          </figure>
        </div>
        <div className="editorial-prose">
          {FOUNDER_STORY.map((text, i) => <p key={i} data-testid={`founder-intro-paragraph-${i}`}>{text}</p>)}
          <Link to="/about#anas-khan" className="editorial-link" data-testid="meet-anas-link">MEET ANAS <ArrowRight size={16} /></Link>
        </div>
      </div>
    </div>
  </section>
);

const RecognitionLogos = () => (
  <div className="editorial-container recognition-logo-directory" data-testid="recognition-logo-directory">
    {RECOGNITION_LOGOS.map((group) => (
      <div className="recognition-logo-group" key={group.id} data-testid={`recognition-logo-group-${group.id}`}>
        <h3 className="editorial-label" data-testid={`recognition-logo-heading-${group.id}`}>{group.title}</h3>
        <div className="recognition-logo-list">
          {group.logos.map((logo) => (
            <figure className={`recognition-logo recognition-logo--${logo.id}`} key={logo.id} data-testid={`recognition-mark-${logo.id}`}>
              <img src={logo.image} alt={logo.alt || `${logo.name} logo`} width="170" height="64" loading="lazy" className="recognition-logo-image" data-testid={`recognition-logo-${logo.id}`} />
              <figcaption className="recognition-logo-name" data-testid={`recognition-name-${logo.id}`}>{logo.name}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    ))}
    <p className="text-xs leading-relaxed text-obsidian/60 max-w-2xl pt-5 border-t border-maroon/15" data-testid="recognition-logo-note">A selection of the publications and organisations mentioned in our story. Their marks identify them; they do not imply endorsement or accreditation.</p>
  </div>
);

const Recognition = () => (
  <section className="editorial-section bg-white border-y border-maroon/15" data-testid="recognition-section">
    <div className="editorial-container narrative-layout">
      <h2 className="editorial-heading" data-testid="recognition-heading">RECOGNISED, TRUSTED & INVITED</h2>
      <div className="editorial-prose">
        {RECOGNITION.map((text, i) => <p key={i} data-testid={`recognition-paragraph-${i}`}>{text}</p>)}
      </div>
    </div>
    <RecognitionLogos />
  </section>
);

const Community = () => (
  <section className="editorial-section community-section" data-testid="instagram-community-section">
    <div className="editorial-container community-layout">
      <figure>
        <img src={ART.cityMap} alt="Historical bird’s-eye illustration of the walled city of Delhi" width="720" height="520" loading="lazy" className="artwork-image" data-testid="community-artwork" />
        <figcaption className="artwork-caption">The City of Delhi Before the Siege. Supplied illustration.</figcaption>
      </figure>
      <div>
        <p className="editorial-label mb-5" data-testid="community-label">FROM DELHI, FOR THE WORLD.</p>
        <h2 className="editorial-heading" data-testid="community-heading">Join a Growing Community</h2>
        <div className="editorial-prose mt-6">
          <p data-testid="instagram-audience-copy">More than 215,000 people follow Unzip Delhi’s storytelling on Instagram, where we document Delhi’s history, culture and everyday life, one story at a time.</p>
          <p data-testid="instagram-community-copy">What began as a way of sharing the city’s overlooked histories has grown into one of India’s largest heritage storytelling and walking platforms, connecting people with Delhi through stories that go beyond the monument and the textbook.</p>
        </div>
        <a href={CONTACT.instagramUrl} target="_blank" rel="noopener noreferrer" className="editorial-link mt-7" data-testid="community-instagram-link">Follow along <ArrowRight size={16} /> {CONTACT.instagram}</a>
      </div>
    </div>
  </section>
);

const SignatureExperiences = () => (
  <section className="editorial-section" data-testid="signature-experiences-section">
    <div className="editorial-container">
      <h2 className="editorial-heading mb-12" data-testid="signature-experiences-heading">SOME OF OUR SIGNATURE EXPERIENCES</h2>
      <div className="signature-layout">
        {EXPERIENCES.map((experience) => (
          <article key={experience.id} data-testid={`signature-experience-${experience.id}`}>
            <figure className="mb-7">
              <img src={experience.image} alt={experience.imageAlt} width={experience.width} height={experience.height} loading="lazy" className="artwork-image" data-testid={`signature-image-${experience.id}`} />
              <figcaption className="artwork-caption">{experience.caption}</figcaption>
            </figure>
            <p className="editorial-label mb-3">{experience.name.toUpperCase()}</p>
            <h3 className="font-serif text-2xl text-maroon leading-snug">{experience.title}</h3>
            <p className="editorial-body mt-4">{experience.description}</p>
            <Link to={`/experiences#${experience.id}`} className="editorial-link mt-6" data-testid={`explore-${experience.id}`}>EXPLORE <ArrowRight size={16} /></Link>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const Organisations = () => (
  <section className="editorial-section bg-white border-t border-maroon/15" data-testid="organisations-section">
    <div className="editorial-container narrative-layout">
      <div>
        <p className="editorial-label mb-5" data-testid="organisations-label">FOR ORGANISATIONS & INSTITUTIONS</p>
        <h2 className="editorial-heading" data-testid="organisations-heading">Experiences designed for curious groups.</h2>
      </div>
      <div className="editorial-prose">
        <p>We work with universities, schools, cultural organisations, foundations, companies and international visitors to create meaningful experiences around India's history and culture.</p>
        <p>From educational field experiences and academic programmes to private group tours and cultural engagements, we can build experiences around your objectives, audience and interests.</p>
        <Link to="/contact?interest=Organisations%20%26%20Institutions" className="editorial-link" data-testid="work-with-us-link">WORK WITH US <ArrowRight size={16} /></Link>
      </div>
    </div>
  </section>
);

const ClosingInvitation = () => (
  <section className="bg-maroon text-ivory" data-testid="closing-invitation-section">
    <img src={BRAND.border} alt="Unzip Delhi wordmark framed by repeating geometric stars" width="1590" height="178" loading="lazy" className="w-full h-auto object-contain" data-testid="home-brand-border" />
    <div className="editorial-container editorial-section narrative-layout">
      <h2 className="editorial-heading text-ivory" data-testid="closing-invitation-heading">COME WALK THROUGH HISTORY WITH US</h2>
      <div className="editorial-prose text-ivory/90">
        <p>Delhi rewards those who slow down, look closely, and listen.</p>
        <p>Let us show you the city as we have always known it, not simply as a destination, but as a living landscape of stories, people, memories and history.</p>
        <Link to="/contact" className="editorial-button editorial-button-light" data-testid="book-your-walk-link">BOOK YOUR WALK <ArrowRight size={16} /></Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Introduction />
      <Offerings />
      <Principles />
      <FounderIntroduction />
      <Recognition />
      <Community />
      <SignatureExperiences />
      <Organisations />
      <ClosingInvitation />
    </main>
  );
}
